import { Box, Flex } from '@chakra-ui/layout'
import { Thead, Tbody, Tr, Th, Td } from '@chakra-ui/react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import numeral from 'numeral'
import { Doughnut } from 'react-chartjs-2'
import type {
  AnalyticsAPIResponse,
  ObjectOfBandwidthCountAndName,
  TransformationPieChartData,
} from '../../interfaces'
import ScrollableTable from './ScrollableTable'
import DoughnutColuredLabel from './DoughnutColuredLabel'

const pieBackgrounds = [
  '#FB8C00',
  '#80DEEA',
  '#5E35B1',
  '#4CAF50',
  '#FF8A80',
  '#B0BEC5',
  '#795548',
  '#BF360C',
  '#C6FF00',
  '#006064',
  '#DCEDC8',
  '#B2EBF2',
  '#5C6BC0',
  '#6A1B9A',
  '#B71C1C',
  '#448AFF',
  '#64FFDA',
  '#AFB42B',
  '#FFD54F',
  '#FF6E40',
]

const getPieChartData = (
  data: ObjectOfBandwidthCountAndName[],
  totalCount: number,
  totalBandwidth: number,
): TransformationPieChartData | null => {
  if (!data.length) {
    return null
  }

  const labels: string[] = []
  const dataset: number[] = []
  const counts: number[] = []
  const bandwidths: number[] = []
  let dataPointCount = 0
  let totalPercentage = 0
  let countSum = 0
  let bandwidthSum = 0
  for (const trData of data) {
    if (trData.count) {
      if (!trData.name) {
        continue
      }
      if (dataPointCount > 8) {
        dataset.push(parseFloat((100 - totalPercentage).toFixed(2)))
        counts.push(totalCount - countSum)
        bandwidths.push(totalBandwidth - bandwidthSum)

        labels.push('Others')
        break
      } else {
        const percentage = parseFloat(
          ((trData.count / totalCount) * 100).toFixed(2),
        )
        totalPercentage += percentage
        dataPointCount++
        countSum += Number(trData.count)
        bandwidthSum += Number(trData.bandwidth)

        dataset.push(percentage)
        counts.push(trData.count)
        bandwidths.push(trData.bandwidth)
        labels.push(trData.name)
      }
    }
  }

  return {
    data: {
      datasets: [
        {
          backgroundColor: pieBackgrounds,
          data: dataset,
        },
      ],
      labels,
    },
    counts,
    bandwidths,
    options: {
      responsive: false,
      plugins: {
        legend: {
          display: false,
        },
      },
    },
  }
}

interface Props {
  data: AnalyticsAPIResponse
  showBandwidth: Boolean
  assetClass: "image" | "video"
}

const TopTransformation = ({
  data,
  showBandwidth,
  assetClass
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const lookupKey = assetClass === "image" ? "transform" : "videoTransform"

  const transformData = data[lookupKey]?.items
  const transformCountTotal = data[lookupKey]?.count ?? 0
  const transformBandwidthTotal = data[lookupKey]?.bandwidth ?? 0

  if (!transformData || !Object.keys(transformData).length) {
    return (
      <Box marginTop="1rem">{t('no_data_available', 'No Data Available')}</Box>
    )
  }

  const pieChartData = getPieChartData(
    transformData,
    transformCountTotal,
    transformBandwidthTotal,
  )

  const tableRows: React.ReactElement[] = []

  if (pieChartData) {
    for (let i = 0; i < pieChartData.data.labels.length; i++) {
      tableRows.push(
        <Tr key={pieChartData.data.labels[i]}>
          <Td>
            <DoughnutColuredLabel
              label={pieChartData.data.labels[i]}
              color={pieChartData.data.datasets[0].backgroundColor[i]}
            />
          </Td>
          <Td>
            {showBandwidth === true
              ? `${numeral(pieChartData.counts[i])
                  .format('0.0 a')
                  .toUpperCase()} requests (${numeral(
                  pieChartData.bandwidths[i],
                )
                  .format('0.0 ib')
                  .replace('i', '')})`
              : `${numeral(pieChartData.counts[i])
                  .format('0.0 a')
                  .toUpperCase()} requests`}
          </Td>
          <Td>{`${pieChartData.data.datasets[0].data[i]} %`}</Td>
        </Tr>,
      )
    }
  }

  return (
    <Box marginTop="2.5rem">
      <Flex justifyContent="center">
        {pieChartData && (
          <Doughnut
            data={pieChartData.data}
            options={pieChartData.options}
            height="300"
          />
        )}
      </Flex>
      <Box marginTop="2.5rem">
        <ScrollableTable>
          <Thead>
            <Tr>
              <Th>{t('transform', 'Transform')}</Th>
              <Th>{t('usage', 'Usage')}</Th>
              <Th>{t('percentage_by_count', 'Percentage By Count')}</Th>
            </Tr>
          </Thead>
          <Tbody>{tableRows}</Tbody>
        </ScrollableTable>
      </Box>
    </Box>
  )
}

export default TopTransformation
